/**
 * `/agents/_surface` — RO-4-03's reachability proof for the design system.
 *
 * ── What this proves, and why it is a route and not a test ──
 *
 * The `@excellent/ui` MAIN barrel (`.`) has to be importable from
 * `excellent/client/` or lane L3 cannot port a single one of its 238 pages
 * without hand-rolling surfaces and breaking the `<Card>` / `DataTable` /
 * `PageHeader` rails. The barrel reaches ~460 modules; the question is not
 * whether it type-checks, it is whether it BUNDLES and RENDERS in a browser with
 * no server-only module smuggled along. Only a real route in the real host,
 * built by the real Vite config, answers that.
 *
 * So this renders the four surface primitives and the three shared formatters
 * named in the item's acceptance — `Card`, `DataTable`, `PageHeader`,
 * `StatusBadge`, `formatUsd`, `formatDate`, `formatRelativeTime` — all off the
 * bare `@excellent/ui` barrel, with fixed local rows. Fixed rows on purpose:
 * the vault → Hono → contract → SWR data path is already proven by `/agents`
 * (RO-0-05) and `/agents/qa` (RO-3-02), and mixing a data assertion in here
 * would mean a fetch failure reads as a barrel failure.
 *
 * The path is `_surface`, not `surface`: a leading-underscore segment is private
 * in Next's App Router, so this cannot ever collide with a page `excellent/app/`
 * grows (D6 — both stacks run side by side and the Next tree is the rollback).
 *
 * ── Delete me when I am redundant ──
 *
 * Once L3 has ported enough real pages that every primitive here is exercised by
 * a product surface, this route has no job. It is a scaffold with an expiry date,
 * like the three transport prove-outs in `agents.tsx` — do NOT grow it into a
 * styleguide (archived at `docs/design/prototypes/` after RO-11-03; product routes deleted).
 */

import { createRoute } from "@tanstack/react-router";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  DataTable,
  PageHeader,
  StatusBadge,
  formatDate,
  formatRelativeTime,
  formatUsd,
  type DataTableColumn,
} from "@excellent/ui";
import { rootRoute } from "./__root";

type ProofRow = {
  id: string;
  what: string;
  status: "todo" | "in_progress" | "done";
  amount: number | null;
  when: string;
};

const ROWS: ProofRow[] = [];

/**
 * Formatters remain reachable through the column renderers without compiling
 * invented product records into the app.
 */
const COLUMNS: DataTableColumn<ProofRow>[] = [
  { key: "what", header: "What", render: (r) => <span className="text-foreground">{r.what}</span> },
  { key: "status", header: "Status", render: (r) => <StatusBadge domain="task" status={r.status} /> },
  {
    key: "amount",
    header: "Amount",
    // No `align: "right"`. Every column left-aligns in this design system —
    // money, counts and dates included — so tables read as clean columns sharing
    // one left edge (docs/design/prototypes/redesign/SYSTEM.md §4.2, gate
    // `check-table-consistency`). A file whose whole purpose is to prove the
    // design system is reachable has to exemplify it.
    render: (r) => <span className="tabular-nums">{formatUsd(r.amount)}</span>,
  },
  { key: "when", header: "Date", render: (r) => <span className="tabular-nums">{formatDate(r.when)}</span> },
  {
    key: "rel",
    header: "Updated",
    render: (r) => <span className="type-subtext text-muted-foreground">{formatRelativeTime(r.when)}</span>,
  },
];

function SurfaceProofPage() {
  return (
    <div className="px-6 py-8 space-y-6">
      <PageHeader
        title="Surface reachability"
        description="RO-4-03 proof · Card, DataTable, PageHeader, StatusBadge and the shared formatters, imported from the @excellent/ui barrel and rendered in the Vite client."
      />

      <Card>
        <CardHeader>
          <CardTitle>Primitives off the barrel</CardTitle>
        </CardHeader>
        {/* p-0: DataTable already carries its own gutter, and `wrapInCard={false}`
            only drops its Card — not its padding. A CardContent with default
            padding around it produces the px-6+px-4 double gutter
            (VERTICAL-RHYTHM-PRD R8, gate `check-card-double-pad`). */}
        <CardContent className="p-0">
          <DataTable
            rows={ROWS}
            columns={COLUMNS}
            rowKey={(r) => r.id}
            wrapInCard={false}
            data-testid="surface-proof-table"
          />
        </CardContent>
      </Card>
    </div>
  );
}

export const agentsSurfaceProofRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/agents/_surface",
  component: SurfaceProofPage,
});
